import { Fragment, useEffect, useMemo } from 'react';
import { useThemeStore } from '../../state/theme';
import { useCalenderState } from '../../state/calender-state';
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdInfoOutline,
} from 'react-icons/md';
import { formatTime, formatTimeInHms } from '../../utils';
import { BtnDefault } from './Buttons';
import { IRaiseMismatch } from '../../api/queries';
import { fetchAgentLogs } from '../../hooks/queries';
import { LoadingErrorState } from '../LoadingErrorState';
import moment from 'moment-timezone';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// A curated list of common IANA timezone names for the dropdown.
const curatedTimezoneDisplay = [
  { id: 'America/New_York', name: 'Eastern Time (US & Canada)' },
  { id: 'America/Chicago', name: 'Central Time (US & Canada)' },
  { id: 'America/Denver', name: 'Mountain Time (US & Canada)' },
  { id: 'America/Los_Angeles', name: 'Pacific Time (US & Canada)' },
  { id: 'Pacific/Honolulu', name: 'Hawaii Time' },
  { id: 'Europe/London', name: 'London, Dublin (GMT/BST)' },
  { id: 'Europe/Berlin', name: 'Berlin, Paris (CET/CEST)' },
  { id: 'Europe/Moscow', name: 'Moscow Time' },
  { id: 'Africa/Cairo', name: 'Egypt Time (EET)' },
  { id: 'Asia/Kolkata', name: 'India Standard Time' },
  { id: 'Asia/Shanghai', name: 'Beijing, Hong Kong' },
  { id: 'Asia/Tokyo', name: 'Tokyo, Seoul' },
  { id: 'Asia/Manila', name: 'Manila Time' },
  { id: 'Australia/Sydney', name: 'Sydney (AEST/AEDT)' },
  { id: 'Etc/UTC', name: 'Coordinated Universal Time (UTC)' },
];

const daysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const CalendarView: React.FC<{
  onCellClick: (date?: Date) => void;
  agentId: string;
  account: string;
  mismatchRequests?: IRaiseMismatch[];
}> = ({ onCellClick, agentId, account, mismatchRequests }) => {
  const {
    currentDate,
    setCurrentDate,
    selectedTimezoneName,
    setSelectedTimezoneName,
  } = useCalenderState();

  const timezoneOptions = useMemo(() => {
    const systemGuessedTimezoneID = moment.tz.guess();
    let options = [...curatedTimezoneDisplay];
    let foundSelectedInCurated = options.some(
      (opt) => opt.id === selectedTimezoneName
    );

    if (selectedTimezoneName && !foundSelectedInCurated) {
      let nameSuffix = ' (Current)';
      if (selectedTimezoneName === systemGuessedTimezoneID) {
        nameSuffix = ' (Your System)';
      }
      options.unshift({
        id: selectedTimezoneName,
        name: `${selectedTimezoneName.replace(/_/g, ' ')}${nameSuffix}`,
      });
    }

    if (
      systemGuessedTimezoneID &&
      systemGuessedTimezoneID !== selectedTimezoneName &&
      !options.some((opt) => opt.id === systemGuessedTimezoneID)
    ) {
      options.unshift({
        id: systemGuessedTimezoneID,
        name: `${systemGuessedTimezoneID.replace(/_/g, ' ')} (Current System)`,
      });
    }

    const uniqueOptions = options.filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.id === option.id)
    );

    return uniqueOptions;
  }, [selectedTimezoneName]);

  const handleTimezoneChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedTimezoneName(event.target.value);
  };

  const offsetInMinutes = useMemo(() => {
    return moment.tz(selectedTimezoneName).utcOffset();
  }, [selectedTimezoneName]);

  // Derive month and year for fetching from currentDate state
  const fetchMonth = (currentDate.getMonth() + 1).toString();
  const fetchYear = currentDate.getFullYear().toString();

  const {
    data,
    isLoading: isAgentLogsLoading,
    isFetching: isAgentLogsFetching,
    error: agentLogsError,
    refetch: refetchAgentLogs,
  } = fetchAgentLogs({
    agentId,
    account,
    month: fetchMonth, // Use month from currentDate state
    year: fetchYear, // Use year from currentDate state
    offset: offsetInMinutes,
  });

  const logs = data?.data ?? [];

  const { isDarkMode } = useThemeStore();

  const handlePrevMonth = () => {
    const prevMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1
    );
    setCurrentDate(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1
    );
    setCurrentDate(nextMonth);
  };

  const renderDays = () => {
    const currentDisplayYear = currentDate.getFullYear();
    const currentDisplayMonth = currentDate.getMonth(); // 0-indexed

    const firstDayOfMonth = new Date(
      currentDisplayYear,
      currentDisplayMonth,
      1
    ).getDay();
    const totalDays = daysInMonth(currentDisplayYear, currentDisplayMonth);

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className={`border h-16 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
        ></div>
      );
    }

    for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
      const dateForCell = moment
        .tz(
          {
            year: currentDisplayYear,
            month: currentDisplayMonth,
            day: dayNum,
          },
          selectedTimezoneName
        )
        .toDate();

      const dailyStats = logs.reduce(
        (stats, log) => {
          if (!log.startTime) return stats;
          const localLogDateTime = moment
            .utc(log.startTime)
            .tz(selectedTimezoneName);
          if (
            localLogDateTime.date() === dayNum &&
            localLogDateTime.month() === currentDisplayMonth &&
            localLogDateTime.year() === currentDisplayYear
          ) {
            stats.workTime += log.duration || 0;
            stats.idleTime += log.idleTime || 0;
            // Sum up all break durations for this log
            const breakDuration =
              log.breaks?.reduce((sum, breakItem) => {
                return sum + (breakItem.duration || 0);
              }, 0) || 0;
            stats.breakTime += breakDuration;
          }
          return stats;
        },
        { workTime: 0, idleTime: 0, breakTime: 0 }
      );

      const { workTime, idleTime, breakTime } = dailyStats;
      const totalTimeWithBreaks = workTime + breakTime;
      const activeTime = workTime; // active hours

      const openSessions = logs.filter((log) => {
        if (!log.startTime) return false;
        const localLogDateTime = moment
          .utc(log.startTime)
          .tz(selectedTimezoneName);
        return (
          localLogDateTime.date() === dayNum &&
          localLogDateTime.month() === currentDisplayMonth &&
          localLogDateTime.year() === currentDisplayYear &&
          log.status === 'open'
        );
      });

      const formattedTime = formatTime(Math.floor(totalTimeWithBreaks * 60));
      const formattedTotalTime = formatTimeInHms(
        Math.floor(totalTimeWithBreaks * 60)
      );
      const formattedActiveTime = formatTimeInHms(Math.floor(activeTime * 60));
      const formattedIdleTime = formatTimeInHms(Math.floor(idleTime * 60));
      const formattedBreakTime = formatTimeInHms(Math.floor(breakTime * 60));

      const currentMismatchRequest = mismatchRequests?.find((mismatch) => {
        if (!mismatch.startTime) return false;
        const localMismatchDateTime = moment
          .utc(mismatch.startTime)
          .tz(selectedTimezoneName);
        return (
          localMismatchDateTime.date() === dayNum &&
          localMismatchDateTime.month() === currentDisplayMonth &&
          localMismatchDateTime.year() === currentDisplayYear
        );
      });

      const mismatchClass = (() => {
        const cellDateStartOfDay = moment
          .tz(
            {
              year: currentDisplayYear,
              month: currentDisplayMonth,
              day: dayNum,
            },
            selectedTimezoneName
          )
          .startOf('day');
        const todayStartOfDay = moment()
          .tz(selectedTimezoneName)
          .startOf('day');
        const threeDaysAgoStartOfDay = moment()
          .tz(selectedTimezoneName)
          .subtract(3, 'days')
          .startOf('day');

        const hasWorkTime = totalTimeWithBreaks > 0;

        if (
          currentMismatchRequest &&
          currentMismatchRequest.status === 'pending'
        )
          return 'text-red-600';
        if (openSessions.length > 0) return 'text-blue-600';
        if (!hasWorkTime) return 'text-gray-500';

        if (cellDateStartOfDay.isBefore(threeDaysAgoStartOfDay))
          return 'text-green-600';

        if (
          cellDateStartOfDay.isSameOrAfter(threeDaysAgoStartOfDay) &&
          cellDateStartOfDay.isSameOrBefore(todayStartOfDay)
        ) {
          return 'text-black';
        }

        return 'text-gray-500'; // Default
      })();

      days.push(
        <div
          key={dayNum}
          className={`border h-16 flex flex-col items-center justify-between p-2 cursor-pointer ${
            isDarkMode
              ? 'hover:bg-gray-600 border-gray-600 text-gray-100'
              : 'hover:bg-blue-100 border-gray-300'
          } ${mismatchClass}`}
          onClick={() => onCellClick(dateForCell)}
        >
          <span>{dayNum}</span>
          {totalTimeWithBreaks > 0 && (
            <div className="relative group flex items-center space-x-1">
              <span className="text-xs font-semibold">{formattedTime}</span>
              <MdInfoOutline className="cursor-pointer" />
              <div
                className="absolute hidden group-hover:block bg-gray-800 text-white text-sm rounded p-2 z-10 w-max text-left whitespace-nowrap"
                style={{
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                Total: {formattedTotalTime}
                <br />
                Active: {formattedActiveTime}
                <br />
                Break: {formattedBreakTime}
                <br />
                Idle: {formattedIdleTime}
              </div>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevMonth(); // Go to next month
      } else if (event.key === 'ArrowRight') {
        handleNextMonth(); // Go to previous month
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentDate]); // Dependency array to re-run effect on currentDate change

  const refetch = () => {
    refetchAgentLogs();
  };

  const isLoaded =
    !isAgentLogsLoading && !isAgentLogsFetching && !agentLogsError;

  return (
    <div
      className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} flex flex-col space-y-8`}
    >
      <LoadingErrorState
        isLoading={isAgentLogsLoading}
        isFetching={isAgentLogsFetching}
        error={agentLogsError}
        refetch={refetch}
        data={logs}
      />
      {isLoaded && (
        <Fragment>
          <div className="flex justify-between items-center gap-2">
            <h2 className="text-2xl w-100 font-bold text-left">
              {new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
                currentDate
              )}{' '}
              {currentDate.getFullYear()}
            </h2>
            <div className="ml-auto flex items-center gap-4">
              <div className="flex items-center">
                <label
                  htmlFor="timezone-select"
                  className="mr-1 text-sm font-normal text-gray-700 whitespace-nowrap"
                >
                  Timezone:
                </label>
                <select
                  id="timezone-select"
                  value={selectedTimezoneName || ''}
                  onChange={handleTimezoneChange}
                  className="p-1 border border-gray-300 rounded-md text-blackFifty text-xs font-normal dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz.id} value={tz.id}>
                      {tz.name}
                    </option>
                  ))}
                </select>
              </div>
              <BtnDefault
                onClick={() =>
                  setCurrentDate(
                    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
                  )
                }
              >
                Current Month:{' '}
                {moment(new Date()).tz(selectedTimezoneName).format('MMM YYYY')}
              </BtnDefault>
            </div>
            <div
              className={`flex items-center border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} rounded-md p-[0.5px]`}
            >
              <MdKeyboardArrowLeft
                className={`w-7 cursor-pointer rounded-l-md px-1 h-6 ${isDarkMode ? 'hover:bg-white hover:fill-black' : 'bg-white hover:bg-gray-200'}`}
                onClick={handlePrevMonth}
              />

              <MdKeyboardArrowRight
                className={`w-7 cursor-pointer rounded-r-md px-1 h-6 ${isDarkMode ? 'hover:bg-white hover:fill-black' : 'bg-white hover:bg-gray-200'}`}
                onClick={handleNextMonth}
              />
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {daysOfWeek.map((day, index) => (
              <div key={index} className="text-sm text-center">
                {day}
              </div>
            ))}
            {renderDays()}
          </div>
        </Fragment>
      )}
      {/* Month Selector */}
    </div>
  );
};

export default CalendarView;
